import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { INLINE_TARGET_ATTR, INLINE_TARGET_ATTR_RNG } from '../../constants/inlineTarget';
import { createInlineTarget, removeInlineTarget } from '../inlineTarget';
function useInlineTarget(editor, initialTarget) {
  const [target, setTarget] = useState(initialTarget);
  const [, forceUpdate] = useReducer(n => !n, false);
  const [refreshKey, updateRefreshKey] = useReducer(n => n + 1, 0);
  const lastTarget = useRef(target);
  useEffect(() => {
    lastTarget.current = target;
  });
  useEffect(() => {
    const handleNodeChange = ({
      element
    }) => {
      // Select the current inline target if it's clicked again
      // This prevents range-to-collapsed selection errors of an inline target
      if (element === lastTarget.current && element.hasAttribute(INLINE_TARGET_ATTR)) {
        editor.selection.select(element);
      }
    };
    editor.on('NodeChange', handleNodeChange);
    return () => {
      editor.off('NodeChange', handleNodeChange);
    };
  }, [editor]);
  const handleSetTarget = useCallback((newTarget, {
    removeTargets = false,
    restoreSelection = false
  } = {}) => {
    if (removeTargets) {
      editor.fire('hsRemoveInlineTargets', {
        curTarget: newTarget
      });
    }
    removeInlineTarget(editor, lastTarget.current, restoreSelection);
    setTarget(newTarget);
  }, [editor]);
  const initInlineTarget = useCallback(({
    fireNodeChange = false,
    removeTargets = true
  } = {}) => {
    const inlineTarget = createInlineTarget(editor);
    if (fireNodeChange) {
      editor.nodeChanged();
    }
    handleSetTarget(inlineTarget, {
      removeTargets
    });
  }, [editor, handleSetTarget]);
  const insertAtInlineTarget = useCallback(onInsert => {
    const curTarget = lastTarget.current;
    if (curTarget && curTarget.hasAttribute(INLINE_TARGET_ATTR)) {
      if (curTarget.hasAttribute(INLINE_TARGET_ATTR_RNG)) {
        // Call onInsert then initialize a new inlineTarget
        onInsert();
        initInlineTarget({
          fireNodeChange: true,
          removeTargets: false
        });
      } else {
        if (!curTarget.parentNode) {
          // If the target has no parent simply close the popover because
          // we probably don't have a valid target
          setTarget(null);
          return;
        }
        // Move cursor to before the inlineTarget, call onInsert,
        // forceUpdate to reuse the same inlineTarget
        const idx = Array.prototype.indexOf.call(curTarget.parentNode.childNodes, curTarget);
        // Since the `inlineTarget` stays the same, we need to force a refresh of the Popover
        updateRefreshKey();
        editor.selection.setCursorLocation(curTarget.parentNode, idx);
        if (editor.undoManager.ignore) {
          // Ignore the implicit undo level from `mceInsertContent` in order to
          // prevent a nodeChange on the potential <a> and instead fired a
          // nodeChange on the inline target
          editor.undoManager.ignore(() => {
            onInsert();
          });
          editor.selection.select(curTarget);
          editor.undoManager.add();
        } else {
          onInsert();
        }
        forceUpdate();
      }
    }
  }, [editor, initInlineTarget]);
  useEffect(() => {
    // Remove this specific inline target when another popover inits/sets a new target
    const removeTarget = ({
      curTarget
    }) => {
      if (lastTarget.current && lastTarget.current !== curTarget) {
        handleSetTarget(null);
      }
    };
    editor.on('hsRemoveInlineTargets', removeTarget);
    return () => {
      editor.off('hsRemoveInlineTargets', removeTarget);
    };
  }, [editor, handleSetTarget]);
  return {
    inlineTarget: target,
    setInlineTarget: handleSetTarget,
    initInlineTarget,
    insertAtInlineTarget,
    refreshKey
  };
}
export default useInlineTarget;